import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'
import { message } from 'antd'

/**
 * 搜索选项接口
 * 支持两种格式：
 * 1. 见名知意格式：{id, name, ...} （推荐）
 * 2. 兼容格式：{label, value, payload}
 */
export interface SearchOption {
  // 新格式字段
  id?: string
  name?: string
  // 兼容字段
  label?: string
  value?: string
  payload?: Record<string, any>
  // 员工字段
  commonName?: string
  domain?: string
  displayNumber?: string
  department?: string
  departmentName?: string
  avatarUrl?: string
  jobStatus?: {
    code: string
    name: string
  }
  leader?: string
  // 城市字段
  code?: string
  provinceCode?: string
  provinceName?: string
  // 其他字段（员工、部门等）
  [key: string]: any
}

interface AsyncSearchSelectProps<T extends SearchOption = SearchOption> {
  /**
   * 组件标签
   */
  label: string

  /**
   * 当前选中的值
   */
  value: string

  /**
   * 当前显示的文本（选中时为 label，未选中时为搜索关键字）
   */
  displayText: string

  /**
   * 搜索函数，接收关键字，返回 Promise<SearchOption[]>
   */
  onSearch: (keyword: string) => Promise<T[]>

  /**
   * 选中选项时的回调
   */
  onChange: (value: string, label: string, option?: T) => void

  /**
   * 占位符
   */
  placeholder?: string

  /**
   * 是否必填
   */
  required?: boolean

  /**
   * 自定义 CSS 类
   */
  className?: string

  /**
   * 自定义选项渲染函数（可选）
   * 如果提供，使用该函数自定义下拉列表项的显示内容
   * 如果未提供，使用默认的单行 label 显示
   */
  formatOption?: (option: T) => React.ReactNode

  /**
   * 加载默认选项的函数（可选）
   * 用于点击时展示热门/默认列表
   * 如果提供，点击输入框时会自动加载并缓存
   */
  loadDefaultOptions?: () => Promise<T[]>

  /**
   * 是否禁用（只读展示，不可交互）
   */
  disabled?: boolean

  /**
   * 判断单个选项是否处于不可选状态（灰显 + 标签 + 点击拦截）。
   * 命中时：下拉项灰显，右侧追加 disabledLabel 标签，点击弹 disabledMessage 提示，不触发 onChange。
   */
  isOptionDisabled?: (option: T) => boolean

  /**
   * 不可选选项右侧的标签文本，默认 "不可选"。
   */
  disabledLabel?: string

  /**
   * 点击不可选选项时的提示文本，默认 "该选项不可选择"。
   */
  disabledMessage?: string
}

/**
 * 异步搜索下拉组件
 * 支持防抖、加载状态、下拉展示等功能
 */
const AsyncSearchSelect = React.forwardRef<
  HTMLDivElement,
  AsyncSearchSelectProps
>(
  (
    {
      label,
      // @ts-ignore - value is received but not used, kept for interface compatibility
      value,
      displayText,
      onSearch,
      onChange,
      placeholder = '输入关键字搜索...',
      required = false,
      className = '',
      formatOption,
      loadDefaultOptions,
      disabled = false,
      isOptionDisabled,
      disabledLabel = '不可选',
      disabledMessage = '该选项不可选择',
    },
    _ref
  ) => {
  // 状态管理
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [options, setOptions] = useState<SearchOption[]>([])
  const [inputValue, setInputValue] = useState(displayText)
  const [error, setError] = useState<string | null>(null)
  const [defaultLoadFailed, setDefaultLoadFailed] = useState(false) // 默认列表加载失败
  const [justSelected, setJustSelected] = useState(false) // 标记是否刚刚选择了选项
  const [isUserTyping, setIsUserTyping] = useState(false) // 标记用户是否正在输入
  const [hasUserModified, setHasUserModified] = useState(false) // 标记用户是否主动修改过输入内容

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // 缓存默认列表数据（用于最少调用原则）
  const defaultOptionsCache = useRef<SearchOption[] | null>(null)

  // 下拉框 portal 定位
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    })
  }, [])

  // 处理搜索（带防抖）
  const handleSearch = useCallback(
    async (keyword: string) => {
      setInputValue(keyword)

      if (keyword.trim() === '') {
        // 当清空输入框时，显示缓存的默认列表或错误状态
        if (defaultOptionsCache.current) {
          setOptions(defaultOptionsCache.current)
        } else {
          setOptions([])
        }
        setError(null)
        return
      }

      setIsLoading(true)
      setError(null)
      setDefaultLoadFailed(false) // 用户开始搜索时，清除默认加载失败标记

      try {
        const results = await onSearch(keyword)
        setOptions(results)
      } catch (err) {
        setError('搜索失败，请稍后重试')
        setOptions([])
      } finally {
        setIsLoading(false)
      }
    },
    [onSearch]
  )

  // 处理输入变化（防抖 300ms）
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value
    setInputValue(keyword)
    setIsUserTyping(true) // 标记用户正在输入
    setJustSelected(false) // 用户开始输入时取消"刚选择"标记

    // 如果用户修改了输入内容，说明要修改已选中的值
    if (keyword !== displayText) {
      setHasUserModified(true)
      // 如果清空了输入框，清空父组件的选中值
      if (keyword.trim() === '' && displayText) {
        onChange('', '', undefined)
      }
      // 如果之前选中了值，现在用户修改了输入框（但未完全清空），也应该清空父组件的选中值
      else if (displayText && keyword.trim() !== '') {
        onChange('', '', undefined)
      }
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      handleSearch(keyword)
      setIsUserTyping(false) // 搜索完成后取消输入标记
    }, 300)
  }

  // 处理选项点击
  const handleSelectOption = (option: SearchOption) => {
    // 不可选选项：弹提示拦截，不触发 onChange
    if (isOptionDisabled?.(option)) {
      message.warning(disabledMessage)
      return
    }

    // 支持多种格式：
    // 1. 员工格式：{commonName, domain, ...}
    // 2. 部门格式：{name, number, ...}
    // 3. 新格式：{id, name, ...}
    // 4. 旧格式：{label, value, payload}

    let value = ''
    let label = ''

    // 优先检查员工格式
    if (option.commonName) {
      value = option.domain || option.displayNumber || option.id || ''
      label = option.commonName
    }
    // 其次检查部门格式
    else if (option.number) {
      value = option.number
      label = option.name || option.shortName || ''
    }
    // 然后检查新格式（含城市格式：{code, name}）
    else if (option.id || option.name) {
      value = option.id || option.code || option.value || ''
      label = option.name || option.label || ''
    }
    // 最后使用旧格式
    else {
      value = option.value || ''
      label = option.label || ''
    }

    onChange(value, label, option)
    setInputValue(label)
    setIsOpen(false)
    setOptions([])
    setJustSelected(true) // 标记已选择，防止立即被重置
    setHasUserModified(false) // 选择后重置修改标记
    setIsUserTyping(false) // 选择完成后取消输入标记
  }

  // 处理输入框获得焦点
  const handleInputFocus = () => {
    setIsOpen(true)
    setJustSelected(false) // 重置标记

    // 如果内容为空且提供了 loadDefaultOptions，则加载默认列表
    if (!inputValue.trim() && loadDefaultOptions) {
      if (defaultOptionsCache.current) {
        // 有缓存：直接使用，不发网络请求
        setOptions(defaultOptionsCache.current)
      } else if (!defaultLoadFailed) {
        // 无缓存且未失败过：发起请求并缓存
        setIsLoading(true)
        loadDefaultOptions()
          .then((results) => {
            defaultOptionsCache.current = results
            setOptions(results)
          })
          .catch(() => {
            setDefaultLoadFailed(true)
            setOptions([])
          })
          .finally(() => {
            setIsLoading(false)
          })
      }
      // 已失败过：不自动重试，等待用户点击"重试"按钮
    } else if (!inputValue.trim()) {
      // 未提供 loadDefaultOptions，也无输入内容时，不显示任何选项
      setOptions([])
    }
  }

  // 重试加载默认列表（由用户主动触发）
  const retryDefaultLoad = useCallback(() => {
    if (!loadDefaultOptions) return
    defaultOptionsCache.current = null
    setDefaultLoadFailed(false)
    setIsLoading(true)
    loadDefaultOptions()
      .then((results) => {
        defaultOptionsCache.current = results
        setOptions(results)
      })
      .catch(() => {
        setDefaultLoadFailed(true)
        setOptions([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [loadDefaultOptions])

  // 处理输入框失焦
  const handleInputBlur = () => {
    // 延迟关闭，以允许点击选项
    setTimeout(() => {
      setIsOpen(false)
      setJustSelected(false) // 重置标记
    }, 200)
  }

  // 处理清空按钮点击
  const handleClearClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // 清空输入值
    setInputValue('')
    setOptions([])
    setIsOpen(false)
    setJustSelected(false)
    setHasUserModified(false)
    setIsUserTyping(false)

    // 通知父组件清空
    onChange('', '', undefined)

    // 聚焦输入框
    inputRef.current?.focus()
  }

  // 点击外部关闭 + 打开时计算定位 + 滚动/缩放时更新定位
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const insideContainer = containerRef.current?.contains(target) ?? false
      const insideDropdown = dropdownRef.current?.contains(target) ?? false
      if (!insideContainer && !insideDropdown) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      updateDropdownPosition()
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', updateDropdownPosition, true)
      window.addEventListener('resize', updateDropdownPosition)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updateDropdownPosition, true)
      window.removeEventListener('resize', updateDropdownPosition)
    }
  }, [isOpen, updateDropdownPosition])

  // 清理 debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // 同步外部 displayText 到内部 inputValue（当 displayText 从外部改变时）
  useEffect(() => {
    // 仅在以下情况下同步：
    // 1. 未刚选择（justSelected = false）
    // 2. 用户没有正在输入（isUserTyping = false）
    // 3. 用户没有主动修改过（hasUserModified = false）
    // 4. displayText 与 inputValue 不同（外部更新了）
    if (!justSelected && !isUserTyping && !hasUserModified && displayText !== inputValue) {
      setInputValue(displayText)
    }
  }, [displayText, justSelected, isUserTyping, hasUserModified])

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* 标签 */}
      <label className="heading-2 mb-clay-sm block">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>

      {/* 输入框 */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={`clay-input w-full pr-20 ${disabled ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
          placeholder={disabled ? '' : placeholder}
          value={inputValue}
          onChange={disabled ? undefined : handleInputChange}
          onFocus={disabled ? undefined : handleInputFocus}
          onBlur={disabled ? undefined : handleInputBlur}
          readOnly={disabled}
          autoComplete="off"
        />

        {/* 右侧图标区域 */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          {/* 清空按钮 - 仅在有值且非禁用时显示 */}
          {inputValue && !disabled && (
            <button
              type="button"
              className="hover:bg-gray-100 rounded-full p-1 transition-colors"
              onClick={handleClearClick}
              onMouseDown={(e) => e.preventDefault()} // 防止触发 blur
              title="清空"
            >
              <svg
                className="w-4 h-4 text-gray-500 hover:text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
          
          {/* 加载/搜索图标 */}
          {isLoading ? (
            <div className="animate-spin">
              <svg
                className="w-5 h-5 text-blue-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          ) : (
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
        </div>
      </div>

      {/* 错误提示（仅用于搜索错误，加载失败错误展示在下拉框内） */}
      {error && <p className="mt-2 text-clay-sm text-red-500">{error}</p>}

      {/* 下拉菜单 - 通过 portal 渲染到 body，避免被 table/overflow 父级裁剪 */}
      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          className="bg-white rounded-clay border border-slate-200 shadow-clay"
          style={dropdownStyle}
        >
          {isLoading ? (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              加载中...
            </div>
          ) : defaultLoadFailed && !inputValue.trim() ? (
            /* 默认列表加载失败：显示错误状态 + 重试按钮 */
            <div className="p-5 text-center">
              <div className="mb-2 flex justify-center">
                <svg className="w-8 h-8 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <p className="text-sm text-slate-500 mb-3">暂无{label}</p>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                onClick={retryDefaultLoad}
                onMouseDown={(e) => e.preventDefault()}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                请重试
              </button>
            </div>
          ) : options.length > 0 ? (
            <ul className="max-h-60 overflow-y-auto">
              {options.map((option, index) => {
                const isDisabledOpt = isOptionDisabled?.(option) ?? false
                return (
                  <li
                    key={`${option.value}-${index}`}
                    className={
                      isDisabledOpt
                        ? 'px-4 py-3 bg-slate-50 cursor-not-allowed border-b border-gray-100 last:border-b-0 flex items-center justify-between gap-2'
                        : 'px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors flex items-center justify-between gap-2'
                    }
                    onClick={() => handleSelectOption(option)}
                  >
                    <div className={isDisabledOpt ? 'flex-1 min-w-0 opacity-60' : 'flex-1 min-w-0'}>
                      {formatOption ? (
                        formatOption(option)
                      ) : (
                        <p className="text-clay-sm font-medium text-gray-800">
                          {option.name || option.label || ''}
                        </p>
                      )}
                    </div>
                    {isDisabledOpt && (
                      <span className="shrink-0 text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 whitespace-nowrap">
                        {disabledLabel}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          ) : inputValue.trim() !== '' ? (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              未找到匹配的结果
            </div>
          ) : (
            <div className="p-4 text-center text-clay-sm text-gray-600">
              请输入关键字搜索
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  )
  }
)

AsyncSearchSelect.displayName = 'AsyncSearchSelect'

export default AsyncSearchSelect
